import React, { useState } from 'react';
import { Play, Check, Linkedin, Twitter, Github, Facebook, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import { personalInfo } from '../mock';
import DataFlowVisualization from './DataFlowVisualization';
import { useTheme } from '../context/ThemeContext';

const Hero = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const { theme } = useTheme();

  const codeText = `# Initialize Data & AI Architect
def get_architect_info():
    return {
        'name': '${personalInfo.name}',
        'role': '${personalInfo.title}',
        'experience': '${personalInfo.yearsOfExperience}+ years',
        'mission': 'Making Data & AI Simple'
    }`;

  const handleRunCell = () => {
    setIsRunning(true);
    setShowOutput(false);
    setDisplayedText('');

    setTimeout(() => {
      setIsRunning(false);
      setShowOutput(true);
      
      // Typing effect for output
      const outputText = JSON.stringify({
        name: personalInfo.name,
        role: personalInfo.title,
        experience: `${personalInfo.yearsOfExperience}+ years`,
        mission: 'Making Data & AI Simple'
      }, null, 2);

      let i = 0;
      const typingInterval = setInterval(() => {
        if (i < outputText.length) {
          setDisplayedText(outputText.substring(0, i + 1));
          i++;
        } else {
          clearInterval(typingInterval);
        }
      }, 20);
    }, 1500);
  };

  const socialIcons = [
    { Icon: Linkedin, url: personalInfo.social.linkedin, label: 'LinkedIn' },
    { Icon: Twitter, url: personalInfo.social.twitter, label: 'Twitter' },
    { Icon: Github, url: 'https://github.com/mkanchwala', label: 'GitHub' },
    { Icon: Facebook, url: personalInfo.social.facebook, label: 'Facebook' },
    { Icon: Instagram, url: personalInfo.social.instagram, label: 'Instagram' }
  ];

  return (
    <section className={`relative min-h-screen pt-20 sm:pt-24 pb-8 sm:pb-12 px-4 sm:px-6 lg:px-8 overflow-hidden ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-[#1B3139] via-[#0f1f26] to-[#1B3139]' 
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'
    }`}>
      {/* Neural Network Data Flow Animation - Works in both themes */}
      <DataFlowVisualization />
      
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: Text Content */}
          <div className="space-y-4 sm:space-y-6 text-center lg:text-left">
            <div className="inline-block">
              <span 
                className="px-3 py-1 bg-[#FF3621]/10 border border-[#FF3621]/30 text-[#FF3621] rounded-full text-xs sm:text-sm font-medium"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {personalInfo.subtitle}
              </span>
            </div>
            
            <h1 
              className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              {personalInfo.name}
            </h1>
            
            <div className="flex items-center justify-center lg:justify-start space-x-4">
              <div className="h-1 w-8 sm:w-12 bg-[#FF3621]"></div>
              <p 
                className={`text-lg sm:text-xl md:text-2xl font-medium ${
                  theme === 'dark' ? 'text-white/70' : 'text-gray-600'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {personalInfo.title}
              </p>
            </div>

            <p 
              className={`text-base sm:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0 ${
                theme === 'dark' ? 'text-white/60' : 'text-gray-500'
              }`}
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              {personalInfo.bio}
            </p>

            <div className="flex items-center justify-center lg:justify-start space-x-3 sm:space-x-4 pt-2 sm:pt-4">
              {socialIcons.map(({ Icon, url, label }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-2 hover:bg-[#FF3621]/20 hover:border-[#FF3621]/50 border rounded-lg transition-all duration-300 group ${
                    theme === 'dark'
                      ? 'bg-white/5 border-white/10'
                      : 'bg-gray-100 border-gray-200'
                  }`}
                  aria-label={label}
                >
                  <Icon className={`h-4 w-4 sm:h-5 sm:w-5 group-hover:text-[#FF3621] transition-colors ${
                    theme === 'dark' ? 'text-white/60' : 'text-gray-600'
                  }`} />
                </a>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-3 sm:gap-4 pt-2 sm:pt-4">
              <Button
                onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#FF3621] text-white hover:bg-[#FF3621]/90 font-medium px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base w-full sm:w-auto"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Let's Connect
              </Button>
              <Button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = '/resume.pdf';
                  link.download = 'Murtaza_Kanchwala_Resume.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                data-testid="download-cv-btn"
                variant="outline"
                className={`font-medium px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base w-full sm:w-auto ${
                  theme === 'dark'
                    ? 'border-white/20 text-white hover:bg-white/10'
                    : 'border-gray-300 text-gray-900 hover:bg-gray-100'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Download CV
              </Button>
            </div>
          </div>

          {/* Right: Interactive Notebook Cell */}
          <div className="relative mt-8 lg:mt-0">
            <div className={`border rounded-lg overflow-hidden shadow-2xl ${
              theme === 'dark'
                ? 'bg-[#0f1f26] border-white/10'
                : 'bg-white border-gray-200'
            }`}>
              {/* Cell Header */}
              <div className={`flex items-center justify-between px-3 sm:px-4 py-2 border-b ${
                theme === 'dark'
                  ? 'bg-white/5 border-white/10'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
                </div>
                <span 
                  className={`text-xs font-mono ${
                    theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                  }`}
                  style={{ fontFamily: 'DM Mono, monospace' }}
                >
                  Cell [1]
                </span>
              </div>

              {/* Code Input */}
              <div className="relative">
                <div className={`absolute left-0 top-0 bottom-0 w-10 sm:w-12 border-r flex items-start justify-center pt-4 ${
                  theme === 'dark'
                    ? 'bg-white/5 border-white/10'
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <Button
                    onClick={handleRunCell}
                    disabled={isRunning}
                    className="bg-transparent hover:bg-[#FF3621]/20 border border-[#FF3621]/50 text-[#FF3621] p-1.5 sm:p-2 h-7 w-7 sm:h-8 sm:w-8 rounded transition-all duration-300"
                  >
                    {isRunning ? (
                      <div className="animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-[#FF3621] border-t-transparent rounded-full"></div>
                    ) : showOutput ? (
                      <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                    ) : (
                      <Play className="h-3 w-3 sm:h-4 sm:w-4" />
                    )}
                  </Button>
                </div>

                <div className="pl-12 sm:pl-16 pr-3 sm:pr-4 py-3 sm:py-4">
                  <pre 
                    className={`text-xs sm:text-sm font-mono overflow-x-auto whitespace-pre-wrap sm:whitespace-pre ${
                      theme === 'dark' ? 'text-white/80' : 'text-gray-800'
                    }`}
                    style={{ fontFamily: 'DM Mono, monospace' }}
                  >
                    <code>{codeText}</code>
                  </pre>
                </div>
              </div>

              {/* Output */}
              {showOutput && (
                <div className={`border-t ${
                  theme === 'dark'
                    ? 'border-white/10 bg-black/30'
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className={`px-3 sm:px-4 py-2 border-b ${
                    theme === 'dark'
                      ? 'bg-white/5 border-white/10'
                      : 'bg-gray-100 border-gray-200'
                  }`}>
                    <span 
                      className={`text-xs font-mono ${
                        theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      Output:
                    </span>
                  </div>
                  <div className="px-3 sm:px-4 py-3 sm:py-4">
                    <pre 
                      className={`text-xs sm:text-sm font-mono overflow-x-auto ${
                        theme === 'dark' ? 'text-[#4ADE80]' : 'text-green-700'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      <code>{displayedText}</code>
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* Decorative Elements - Hidden on mobile */}
            <div className="hidden sm:block absolute -z-10 top-10 -right-10 w-72 h-72 bg-[#FF3621]/10 rounded-full blur-3xl"></div>
            <div className="hidden sm:block absolute -z-10 bottom-10 -left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
